import * as THREE from "three";
import { isNonCollidable } from "./ballistics";

export type OnHitCallback = (
  point: THREE.Vector3,
  blastRadius: number,
  hitObject: THREE.Object3D | null,
  weaponId: string,
  blastDamage: number,
) => void;

const POOL_SIZE = 20;
const _seg = new THREE.Vector3();
const _raycaster = new THREE.Raycaster();

type Projectile = {
  mesh: THREE.Mesh;
  velocity: THREE.Vector3;
  blastRadius: number;
  blastDamage: number;
  weaponId: string;
  lifeLeft: number;
  active: boolean;
  prevPos: THREE.Vector3;
};

export class ProjectileManager {
  private readonly pool: Projectile[] = [];

  constructor(private readonly scene: THREE.Scene) {
    const mat = new THREE.MeshStandardMaterial({ color: 0xffdd88, emissive: 0xffaa00, emissiveIntensity: 1.5 });
    for (let i = 0; i < POOL_SIZE; i++) {
      const mesh = new THREE.Mesh(new THREE.SphereGeometry(0.3, 6, 6), mat);
      mesh.visible = false;
      mesh.userData["isProjectile"] = true;
      scene.add(mesh);
      this.pool.push({
        mesh,
        velocity: new THREE.Vector3(),
        blastRadius: 0,
        blastDamage: 1,
        weaponId: "projectile",
        lifeLeft: 0,
        active: false,
        prevPos: new THREE.Vector3(),
      });
    }
  }

  spawn(from: THREE.Vector3, dir: THREE.Vector3, speed: number, blastRadius: number, weaponId: string, blastDamage = 1): void {
    const slot = this.pool.find((p) => !p.active);
    if (!slot) return;
    slot.mesh.position.copy(from);
    slot.prevPos.copy(from);
    slot.velocity.copy(dir).multiplyScalar(speed);
    slot.blastRadius = blastRadius;
    slot.blastDamage = blastDamage;
    slot.weaponId = weaponId;
    slot.lifeLeft = 6;
    slot.active = true;
    slot.mesh.visible = true;
  }

  update(dt: number, onHit: OnHitCallback): void {
    for (const p of this.pool) {
      if (!p.active) continue;
      p.lifeLeft -= dt;

      const step = p.velocity.clone().multiplyScalar(dt);
      _seg.copy(step);
      const dist = _seg.length();

      _raycaster.set(p.prevPos, _seg.normalize());
      _raycaster.far = dist + 1;
      const hits = _raycaster.intersectObjects(this.scene.children, true);
      const hit = hits.find(
        (h) => h.distance >= 0 && h.distance <= dist + 1 && !isNonCollidable(h.object),
      );

      if (hit || p.lifeLeft <= 0) {
        const point = hit ? hit.point : p.mesh.position;
        onHit(point, p.blastRadius, hit?.object ?? null, p.weaponId, p.blastDamage);
        p.active = false;
        p.mesh.visible = false;
      } else {
        p.prevPos.copy(p.mesh.position);
        p.mesh.position.add(step);
      }
    }
  }

  dispose(): void {
    for (const p of this.pool) this.scene.remove(p.mesh);
  }
}
